import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Button, Stack, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { formatCurrency } from '../../utils/formatters';
import { getGSTReport, exportGSTReport } from '../../api/endpoints/reports';
import { downloadBlob } from '../../utils/exportHelpers';

const GSTReport = () => {
  const [period, setPeriod] = useState(new Date().getFullYear().toString());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await getGSTReport({ period });
      setReport(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load GST report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await downloadBlob(exportGSTReport({ period }), `gst-report-${period}.csv`);
      toast.success('GST report exported');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to export report');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const gstData = report?.monthly || report?.data || [];

  return (
    <Box>
      <PageHeader title="GST Report" subtitle="Goods and Services Tax summary">
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>Export GST Report</Button>
      </PageHeader>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Period</InputLabel>
          <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value)}>
            <MenuItem value={new Date().getFullYear().toString()}>Current Year</MenuItem>
            <MenuItem value={(new Date().getFullYear() - 1).toString()}>Previous Year</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {report && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>{formatCurrency(report.totalRevenue || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'secondary.main' }}>{formatCurrency(report.gst18 || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">GST @18%</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6">{formatCurrency(report.gst12 || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">GST @12%</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6">{report.totalTransactions || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Total Transactions</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>GST Collection Details</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Taxable Amount</TableCell>
                      <TableCell align="right">CGST</TableCell>
                      <TableCell align="right">SGST</TableCell>
                      <TableCell align="right">IGST</TableCell>
                      <TableCell align="right">Total GST</TableCell>
                      <TableCell align="right">Transactions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {gstData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">No data found</TableCell>
                      </TableRow>
                    ) : gstData.map((row, i) => (
                      <TableRow key={row._id || row.period || i} hover>
                        <TableCell>{row.period || row.month || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(row.taxableAmount || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.cgst || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.sgst || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.igst || 0)}</TableCell>
                        <TableCell align="right"><Typography fontWeight={600}>{formatCurrency((row.cgst || 0) + (row.sgst || 0) + (row.igst || 0))}</Typography></TableCell>
                        <TableCell align="right">{row.transactions || row.count || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default GSTReport;